import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "@/components/admin-layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { 
  User, 
  ChevronLeft, 
  ChevronRight, 
  Loader2, 
  Check, 
  X, 
  ExternalLink,
  Github
} from "lucide-react";
import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";

interface Code {
  id: number;
  title: string;
  description: string;
  github_link: string;
  image_url: string | null;
  userId: number;
  visible: number;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    displayName: string | null;
    email: string;
    avatarUrl: string | null;
  };
}

interface PaginatedResponse {
  codes: Code[];
  total: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 10;

export default function AdminCodesPage() {
  const [page, setPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<PaginatedResponse>({
    queryKey: ["/api/admin/codes", { page, showAll }],
    queryFn: async () => {
      const response = await fetch(`/api/admin/codes?page=${page}&showAll=${showAll}`);
      if (!response.ok) {
        throw new Error("Failed to fetch codes");
      }
      return response.json();
    },
  });

  const updateVisibilityMutation = useMutation({
    mutationFn: async ({ id, visible }: { id: number; visible: number }) => {
      const response = await fetch(`/api/admin/codes/${id}/visibility`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ visible }),
      });

      if (!response.ok) {
        throw new Error("Failed to update code visibility");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/codes"] });
      toast({
        title: "Success",
        description: "Code visibility updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleApprove = (code: Code) => {
    updateVisibilityMutation.mutate({ id: code.id, visible: 1 });
  };

  const handleReject = (code: Code) => {
    updateVisibilityMutation.mutate({ id: code.id, visible: 0 });
  };

  const openGithubLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (data && page < Math.ceil(data.total / PAGE_SIZE)) {
      setPage(page + 1);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Manage Codes</h1>
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-400">Show all codes</span>
            <Switch
              checked={showAll}
              onCheckedChange={setShowAll}
            />
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>Codes {!showAll && "Pending Review"}</CardTitle>
              <CardDescription>
                {showAll 
                  ? "Showing all codes" 
                  : "Showing codes that need to be reviewed before being displayed to users"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data?.codes.length === 0 ? (
                <div className="text-center py-8 text-zinc-400">
                  No codes {!showAll && "pending review"} found
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>GitHub Link</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data?.codes.map((code) => (
                        <TableRow key={code.id}>
                          <TableCell className="font-medium">{code.title}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-6 w-6">
                                <AvatarImage src={code.user?.avatarUrl || ""} />
                                <AvatarFallback>
                                  <User className="h-4 w-4" />
                                </AvatarFallback>
                              </Avatar>
                              <span>
                                {code.user?.displayName || code.user?.email.split("@")[0]}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openGithubLink(code.github_link)}
                            >
                              <Github className="mr-2 h-4 w-4" />
                              View
                            </Button>
                          </TableCell>
                          <TableCell>
                            {code.visible === 1 ? (
                              <Badge variant="success" className="bg-green-600">Visible</Badge>
                            ) : (
                              <Badge variant="outline">Hidden</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {new Date(code.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {code.visible === 0 ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleApprove(code)}
                                  className="bg-green-600/20 hover:bg-green-600/30 text-green-500"
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  Approve
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleReject(code)}
                                  className="bg-red-600/20 hover:bg-red-600/30 text-red-500"
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Hide
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <div className="flex items-center justify-end space-x-2 py-4">
                    <div className="text-sm text-zinc-400">
                      Page {page} of {Math.max(1, Math.ceil((data?.total || 0) / PAGE_SIZE))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePreviousPage}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={
                        !data || page >= Math.ceil(data.total / PAGE_SIZE)
                      }
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
